import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../css/RangeSlider.css';

const RangeSlider = ({ 
  min, 
  max, 
  step = 1, 
  value = [min, max], 
  onChange, 
  formatValue = (val) => val,
  className = ""
}) => {
  const [localMin, setLocalMin] = useState(value[0] || min);
  const [localMax, setLocalMax] = useState(value[1] || max);
  const [isDragging, setIsDragging] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);
  const [editingMin, setEditingMin] = useState(false);
  const [editingMax, setEditingMax] = useState(false);
  const [minInputValue, setMinInputValue] = useState(String(value[0] || min));
  const [maxInputValue, setMaxInputValue] = useState(String(value[1] || max));
  const sliderRef = useRef(null);
  const localMinRef = useRef(localMin);
  const localMaxRef = useRef(localMax);
  const isDraggingRef = useRef(isDragging);
  const activeThumbRef = useRef(activeThumb);

  // Keep refs in sync with state
  useEffect(() => {
    localMinRef.current = localMin;
    localMaxRef.current = localMax;
    isDraggingRef.current = isDragging;
    activeThumbRef.current = activeThumb;
  }, [localMin, localMax, isDragging, activeThumb]);

  // Update local state when external value changes (but not during dragging)
  useEffect(() => {
    if (!isDragging) {
      setLocalMin(value[0] || min);
      setLocalMax(value[1] || max);
      if (!editingMin) {
        setMinInputValue(String(value[0] || min));
      }
      if (!editingMax) {
        setMaxInputValue(String(value[1] || max));
      }
    }
  }, [value, min, max, isDragging, editingMin, editingMax]);

  const getPercentage = (val) => ((val - min) / (max - min)) * 100;

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const newValue = min + (percentage / 100) * (max - min);
    const steppedValue = Math.round(newValue / step) * step;

    if (activeThumbRef.current === 'min') {
      const newMin = Math.min(steppedValue, localMaxRef.current);
      // Update both state and ref immediately
      setLocalMin(newMin);
      localMinRef.current = newMin;
      // Immediate callback for real-time updates
      onChange && onChange([newMin, localMaxRef.current]);
    } else if (activeThumbRef.current === 'max') {
      const newMax = Math.max(steppedValue, localMinRef.current);
      // Update both state and ref immediately
      setLocalMax(newMax);
      localMaxRef.current = newMax;
      // Immediate callback for real-time updates
      onChange && onChange([localMinRef.current, newMax]);
    }
  }, [min, max, step, onChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setActiveThumb(null);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback((e, thumb) => {
    e.preventDefault();
    setIsDragging(true);
    setActiveThumb(thumb);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, handleMouseUp]);

  const handleTrackClick = (e) => {
    if (isDragging) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = ((e.clientX - rect.left) / rect.width) * 100;
    const clickValue = min + (percentage / 100) * (max - min);
    const steppedValue = Math.round(clickValue / step) * step;

    // Determine which thumb to move based on proximity
    const distToMin = Math.abs(clickValue - localMin);
    const distToMax = Math.abs(clickValue - localMax);

    if (distToMin < distToMax) {
      const newMin = Math.min(steppedValue, localMax);
      setLocalMin(newMin);
      onChange && onChange([newMin, localMax]);
    } else {
      const newMax = Math.max(steppedValue, localMin);
      setLocalMax(newMax);
      onChange && onChange([localMin, newMax]);
    }
  };

  const handleMaxInputChange = (e) => {
    setMaxInputValue(e.target.value);
  };

  const handleMinInputChange = (e) => {
    setMinInputValue(e.target.value);
  };

  const handleMinInputBlur = () => {
    setEditingMin(false);
    let newMin = Number(minInputValue);
    if (isNaN(newMin) || newMin < min) {
      newMin = min;
    }
    if (newMin > localMax) {
      newMin = localMax;
    }
    setLocalMin(newMin);
    setMinInputValue(String(newMin));
    onChange && onChange([newMin, localMax]);
  };

  const handleMaxInputBlur = () => {
    setEditingMax(false);
    let newMax = Number(maxInputValue);
    if (isNaN(newMax) || newMax > max) {
      newMax = max;
    }
    if (newMax < localMin) {
      newMax = localMin;
    }
    setLocalMax(newMax);
    setMaxInputValue(String(newMax));
    onChange && onChange([localMin, newMax]);
  };

  const handleMinInputFocus = () => {
    setEditingMin(true);
  };

  const handleMaxInputFocus = () => {
    setEditingMax(true);
  };

  const handleMinKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleMaxKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const minPercentage = getPercentage(localMin);
  const maxPercentage = getPercentage(localMax);

  return (
    <div className={`range-slider-component ${className} ${isDragging ? 'dragging' : ''}`}>
      <div 
        className="range-slider-track-container"
        ref={sliderRef}
        onClick={handleTrackClick}
      >
        <div className="range-slider-track">
          <div 
            className="range-slider-range"
            style={{
              left: `${minPercentage}%`,
              width: `${maxPercentage - minPercentage}%`
            }}
          />
          
          <div
            className={`range-slider-thumb min-thumb ${activeThumb === 'min' ? 'active' : ''}`}
            style={{ left: `${minPercentage}%` }}
            onMouseDown={(e) => handleMouseDown(e, 'min')}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={localMin}
            tabIndex={0}
          />
          
          <div
            className={`range-slider-thumb max-thumb ${activeThumb === 'max' ? 'active' : ''}`}
            style={{ left: `${maxPercentage}%` }}
            onMouseDown={(e) => handleMouseDown(e, 'max')}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={localMax}
            tabIndex={0}
          />
        </div>
      </div>
      
      <div className="range-slider-labels">
        <input 
          type="number"
          className="range-value min-value"
          value={minInputValue}
          onChange={handleMinInputChange}
          onBlur={handleMinInputBlur}
          onFocus={handleMinInputFocus}
          onKeyDown={handleMinKeyDown}
          min={min}
          max={localMax}
          step={step}
        />
        <input 
          type="number"
          className="range-value max-value"
          value={maxInputValue}
          onChange={handleMaxInputChange}
          onBlur={handleMaxInputBlur}
          onFocus={handleMaxInputFocus}
          onKeyDown={handleMaxKeyDown}
          min={localMin}
          max={max}
          step={step}
        />
      </div>
    </div>
  );
};

export default RangeSlider;

